import { FC, ReactNode } from 'react';
import { styled } from '@mui/material';
import CBContainer, { ContainerProps as CBContainerProps } from '../Container';
import Container from './Container';
import Controls from './Controls';
import Menu from './Menu';
import MobileMenu from './MobileMenu';
import MenuItem from './MenuItem';
import Button from './Button';
import UserButton from './UserButton';
import LoginButton from './LoginButton';
import LogoButton from './LogoButton';

const StyledAppBar = styled('div', {
  name: 'AppBar',
  slot: 'root',
})(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.common.white,
  height: '56px',
  minWidth: '320px',
  [theme.breakpoints.down('sm')]: {
    height: '48px',
  },
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledAppBarWrapper = styled('div', {
  name: 'AppBar',
  slot: 'wrapper',
})(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '56px',
  [theme.breakpoints.down('sm')]: {
    height: '48px',
  },
}));

export interface AppBarProps {
  children: ReactNode;
  /**
   * AppBar includes a `@o2/common-business/Container`. You can set this container's `maxWidth`, or fall back to the default
   * 'full-wide'.
   */
  maxWidth?: CBContainerProps['variant'];
}

type AppBar = FC<AppBarProps> & {
  Button: typeof Button;
  UserButton: typeof UserButton;
  LoginButton: typeof LoginButton;
  LogoButton: typeof LogoButton;
  Container: typeof Container;
  Controls: typeof Controls;
  Menu: typeof Menu;
  MenuItem: typeof MenuItem;
  MobileMenu: typeof MobileMenu;
};

/**
 * AppBar is base O2 control. This component should be placed exclusively in the `Layout.Header` and must always contain at least one child.
 *
 * AppBar include subcomponents: _container, controls, LogoButton, Menu, Menu Item, MobileMenu, Button, UserButton_
 *
 * **Example (MojeO2)**
 *
 * ```tsx
 * import { FC, useState } from 'react';
 * import { useMediaQuery, useTheme } from '@mui/material';
 * import {
 *   Basket,
 *   Coins,
 *   Contract,
 *   CustomerAvatarResident,
 *   Internet,
 *   Key,
 *   LogoMojeO2,
 *   Service,
 *   Settings,
 * } from '@o2/icons';
 * import AppBar from '@o2/common-business/AppBar';
 * import SideBarMenu from '@o2/common-business/SideBarMenu';
 * import { useNavigate } from 'react-router';
 *
 * const Header: FC = () => {
 *   const navigate = useNavigate();
 *   const theme = useTheme();
 *   const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
 *   const [logged, setLogged] = useState<boolean>(false);
 *   const [userPopperOpen, setUserPopperOpen] = useState<boolean>(false);
 *
 *   return (
 *     <AppBar maxWidth="full-wide">
 *       <AppBar.Container>
 *         <AppBar.LogoButton onClick={() => navigate('/')}>
 *           <LogoMojeO2 color="white" />
 *         </AppBar.LogoButton>
 *         <AppBar.Menu>
 *           <AppBar.MenuItem active>Přehled</AppBar.MenuItem>
 *           <AppBar.MenuItem>Vyúčtování</AppBar.MenuItem>
 *           <AppBar.MenuItem>Dokumenty</AppBar.MenuItem>
 *         </AppBar.Menu>
 *       </AppBar.Container>
 *       <AppBar.Controls>
 *         <AppBar.Button showBadge tooltip="Můj košík" icon={<Basket />}>
 *           Košík
 *         </AppBar.Button>
 *         {!smallScreen && <AppBar.Button icon={<Internet />}>O2.cz</AppBar.Button>}
 *         {logged ? (
 *           <AppBar.UserButton
 *             onClick={() => setUserPopperOpen(!userPopperOpen)}
 *             customerType="business"
 *             showChevron
 *             chevronOpen={userPopperOpen}
 *             title="Zákazník: Jan Novák"
 *             subtitle="O2 ID: novak@o2.cz"
 *             tooltip="Můj tooltip"
 *           />
 *         ) : (
 *           <AppBar.LoginButton onClick={() => setLogged(true)} icon={<CustomerAvatarResident height={28} />}>
 *             Přihlásit
 *           </AppBar.LoginButton>
 *         )}
 *         {smallScreen && (
 *           <AppBar.MobileMenu logo={<LogoMojeO2 color="blue" />}>
 *             <SideBarMenu.List>
 *               <SideBarMenu.Button active title="Přehled" icon={<Service />} onClick={() => alert('ok')} />
 *               <SideBarMenu.Button title="Vyúčtování" icon={<Coins />} />
 *               <SideBarMenu.Button title="Dokumenty" icon={<Contract />} />
 *             </SideBarMenu.List>
 *             <SideBarMenu.List>
 *               <SideBarMenu.Button title="Přístupy" icon={<Key />} />
 *               <SideBarMenu.Button title="Nastavení" icon={<Settings />} />
 *             </SideBarMenu.List>
 *           </AppBar.MobileMenu>
 *         )}
 *       </AppBar.Controls>
 *     </AppBar>
 *   );
 * };
 *
 * export default Header;
 * ```
 *
 * **Base structure**
 *
 * ```tsx
 *  <AppBar>
 *    <AppBar.Container>
 *      // Left side of AppBar - for Logo and menu
 *    </AppBar.Container>
 *    <AppBar.Controls>
 *      // Right side of AppBar - for controls, login button, mobile Menu, etc.
 *    </AppBar.Controls>
 *  </AppBar>
 * ```
 *
 * **NOTE**
 *
 * - _**Menu**_: The menu is automatically hidden on display md and smaller. You need implement _MobileMenu_
 * - _**MobileMenu**_: Displays the Menu button automatically. After clicking on the screen, a vertical menu will appear on the left side.
 * - _**Button**_: Button assigned to the `AppBar.Controls` section. On the screen lg (default) and smaller, if the button contains an icon, only the icon is displayed. The breakpoint for shrinking the button can be adjusted via the `breakpoint` prop.
 * - _**LogoButton**_: If your application has a specially customizable logo (such as MojeO2), you can place the logo in `AppBar.LogoButton`. This is not needed when using `O2AppLogo`.
 */
const AppBar: AppBar = ({ children, maxWidth = 'full-wide', ...props }) => {
  return (
    <StyledAppBar {...props}>
      <CBContainer disableMarginY variant={maxWidth}>
        <StyledAppBarWrapper>{children}</StyledAppBarWrapper>
      </CBContainer>
    </StyledAppBar>
  );
};

AppBar.Button = Button;
AppBar.UserButton = UserButton;
AppBar.LoginButton = LoginButton;
AppBar.LogoButton = LogoButton;
AppBar.Container = Container;
AppBar.Controls = Controls;
AppBar.Menu = Menu;
AppBar.MenuItem = MenuItem;
AppBar.MobileMenu = MobileMenu;

export default AppBar;
